// lib/screen-utils.ts
// Pure functions for building QDataTable props from ScreenDefinition + ScreenState
// Q-187: Screen Administrator

import type { ReactNode } from 'react';
import type { QColumnDef, QDataTableProps, QRowAction } from '@/components/tables';
import type {
  ScreenDefinition,
  ScreenState,
  BuildTablePropsOptions,
  ScreenRowAction,
} from '@/types/screen-definition';
import type { PaginatedResponse } from '@/types';
import { getCellRenderer } from '@/components/tables/cell-renderers';
import type { LucideIcon } from 'lucide-react';
import {
  Eye, Pencil, Trash2, Ticket, Users, ClipboardList,
  MessageSquare, FolderKanban, Search, Plus, Settings,
  Building2, Calendar, Clock, Check,
} from 'lucide-react';

// --- Static Icon Registry (avoids wildcard import of 1400+ icons) ---

const ICON_REGISTRY: Record<string, LucideIcon> = {
  Eye, Pencil, Trash2, Ticket, Users, ClipboardList,
  MessageSquare, FolderKanban, Search, Plus, Settings,
  Building2, Calendar, Clock, Check,
};

// --- Nested Value Helper ---

/**
 * Resolves dot-notation field paths from an object.
 * e.g. getNestedValue(row, 'asignado_a.nombre') → row.asignado_a?.nombre
 */
export function getNestedValue(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== 'object') return undefined;
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null) return undefined;
    if (Array.isArray(current) || typeof current !== 'object') return undefined;
    if (!Object.prototype.hasOwnProperty.call(current, part)) return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

// --- Lucide Icon Resolver ---

function resolveLucideIcon(name: string): LucideIcon | undefined {
  const icon = ICON_REGISTRY[name];
  if (!icon && import.meta.env.DEV) {
    console.warn(
      `[screen-utils] Icon "${name}" not found in registry. ` +
      `Add it to ICON_REGISTRY in screen-utils.ts.`
    );
  }
  return icon;
}

// --- Column Builder ---

function buildColumns<T>(
  definition: ScreenDefinition,
  customRenderers?: Record<string, (row: T) => ReactNode>,
): QColumnDef<T>[] {
  return definition.columns.map((col) => {
    const base: QColumnDef<T> = {
      key: col.key,
      header: col.header,
      sortable: col.sortable,
      locked: col.locked,
      defaultVisible: col.defaultVisible,
      width: col.width,
    };

    // Custom renderer escape hatch
    if (col.type === 'custom') {
      const customFn = customRenderers?.[col.key];
      if (!customFn && import.meta.env.DEV) {
        console.warn(
          `[buildColumns] Column "${col.key}" is type "custom" in screen "${definition.id}" ` +
          `but no renderer found in customRenderers. Column will render empty.`
        );
      }
      if (customFn) {
        base.render = customFn;
      }
      return base;
    }

    // Registry-based renderer
    const renderer = getCellRenderer(col.type);
    if (renderer) {
      base.render = (row: T) => {
        const value = getNestedValue(row as Record<string, unknown>, col.key);
        return renderer({
          value,
          row: row as Record<string, unknown>,
          config: col.config as Record<string, unknown> | undefined,
        });
      };
    }

    return base;
  });
}

// --- Row Actions Builder ---

function buildRowActions<T>(
  definition: ScreenDefinition,
  screenActions: ScreenRowAction[] | undefined,
  actionHandlers: Record<string, (row: T) => void> | undefined,
): QRowAction<T>[] | undefined {
  if (!screenActions?.length || !actionHandlers) return undefined;

  return screenActions.map((sa) => {
    const icon = sa.icon ? resolveLucideIcon(sa.icon) : undefined;
    const showField = sa.show;
    return {
      label: sa.label,
      icon,
      onClick: (row: T) => {
        const handler = actionHandlers[sa.action];
        if (!handler) {
          if (import.meta.env.DEV) {
            console.warn(
              `[buildRowActions] No handler for action "${sa.action}" in screen "${definition.id}".`
            );
          }
          return;
        }
        handler(row);
      },
      variant: sa.variant,
      show: showField
        ? (row: T) => !!getNestedValue(row as Record<string, unknown>, showField)
        : undefined,
    };
  });
}

// --- Main Build Function ---

/**
 * Pure function that builds all props needed for <QDataTable />.
 * Handles both server-side and client-side pagination modes.
 */
export function buildTableProps<T>(
  definition: ScreenDefinition,
  state: ScreenState,
  queryResult: { data: T[] | PaginatedResponse<T> | undefined; isLoading: boolean },
  options: BuildTablePropsOptions<T>,
): Omit<QDataTableProps<T>, 'headerActions'> {
  const { getId, customRenderers, actionHandlers, onRowClick } = options;
  const { data: rawData, isLoading } = queryResult;

  // Resolve data and total based on pagination mode
  let data: T[];
  let totalItems: number;

  if (definition.pagination.mode === 'server') {
    // Server-side: data is PaginatedResponse<T>
    const paginated = rawData as PaginatedResponse<T> | undefined;
    if (paginated !== undefined && !Array.isArray(paginated.items)) {
      if (import.meta.env.DEV) {
        console.error(
          `[buildTableProps] Screen "${definition.id}" is mode "server" but received data ` +
          `without an "items" array. Expected { items: T[], total: number }.`
        );
      }
      data = [];
      totalItems = 0;
    } else {
      data = paginated?.items ?? [];
      totalItems = paginated?.total ?? 0;
    }
  } else {
    // Client-side: data is T[], paginate locally
    if (rawData !== undefined && !Array.isArray(rawData)) {
      if (import.meta.env.DEV) {
        console.error(
          `[buildTableProps] Screen "${definition.id}" is mode "client" but received non-array data.`
        );
      }
      data = [];
      totalItems = 0;
    } else {
      const allData = (rawData as T[] | undefined) ?? [];
      totalItems = allData.length;
      const safePage = Math.max(1, state.page);
      const safePageSize = Math.max(1, state.pageSize);
      data = allData.slice(
        (safePage - 1) * safePageSize,
        safePage * safePageSize,
      );
    }
  }

  // Build columns from screen definition + custom renderers
  const columns = buildColumns<T>(definition, customRenderers);

  // Build row actions
  const rowActions = buildRowActions<T>(definition, definition.rowActions, actionHandlers);

  // Resolve empty state icon
  const emptyIcon = definition.empty?.icon
    ? resolveLucideIcon(definition.empty.icon)
    : undefined;

  return {
    data,
    totalItems,
    isLoading,
    getId,
    columns,
    title: definition.title,
    itemLabel: definition.itemLabel,

    // Search
    search: definition.search?.enabled ? state.search : undefined,
    onSearchChange: definition.search?.enabled ? state.setSearch : undefined,
    searchPlaceholder: definition.search?.placeholder,

    // Sorting
    sort: state.sort,
    order: state.order,
    onSortChange: state.setSort,

    // Pagination
    page: state.page,
    pageSize: state.pageSize,
    onPageChange: state.setPage,
    onPageSizeChange: state.setPageSize,

    // Quick filters
    quickFilters: definition.quickFilters,
    quickFilterValues: state.quickFilterValues,
    onQuickFilterChange: state.setQuickFilterValues,

    // Advanced filters
    filterSections: definition.filterSections,
    filterValues: state.filterValues,
    onApplyFilters: state.setFilterValues,

    // Row interaction
    onRowClick,
    rowActions,

    // Empty state
    emptyMessage: definition.empty?.message,
    emptyIcon,
  };
}
